/**
 * Grid calculation utilities for BulkSampleEntry
 * Handles color coding, threshold logic, and cell calculations
 */

/**
 * Get color class for a cell based on value and thresholds
 * @param {string} value - Cell value
 * @param {string} group - Organism group
 * @param {string} species - Organism species
 * @param {string} organismKey - Unique organism key
 * @param {string} location - Location/column identifier
 * @param {Object} colorOverrides - Manual color overrides object
 * @param {Array} organisms - Array of organisms with threshold values
 * @returns {string} Tailwind CSS class string for cell background
 */
export const getColorForValue = (value, group, species, organismKey, location, colorOverrides = {}, organisms = []) => {
  if (value === null || value === undefined || value === '') return 'bg-white';
  
  const overrideKey = `${organismKey}_${location}`;
  const strValue = String(value).trim();
  
  // Parse numeric value
  const isBelow = strValue.startsWith('<');
  const cleanValue = strValue.replace(/[<>\s\u00A0]/g, '').replace(/,/g, '.');
  const numValue = parseFloat(cleanValue);
  
  if (isBelow || strValue === '0' || strValue === '+') return 'bg-emerald-100 text-emerald-900';
  if (isNaN(numValue)) return 'bg-white';
  if (numValue === 0) return 'bg-emerald-100 text-emerald-900';
  
  // Find organism thresholds (including sum rows which may have group_thresholds)
  const organism = organisms.find(o => o.group === group && o.species === species && !o.isTotal);
  
  // Calculate threshold-based color
  let thresholdColor = null;
  if (organism && organism.threshold_red != null) {
    const tY = organism.threshold_yellow || 0;
    const tO = organism.threshold_orange || 0;
    const tR = organism.threshold_red || 0;
    if (numValue >= tR) thresholdColor = 'red';
    else if (numValue >= tO) thresholdColor = 'orange';
    else if (numValue >= tY) thresholdColor = 'yellow';
    else thresholdColor = 'green';
  }
  
  // Use manual override if it differs from threshold (= user explicitly changed it)
  const override = colorOverrides[overrideKey];
  const effectiveColor = (override && thresholdColor && override !== thresholdColor) ? override : (thresholdColor || override || 'green');
  
  const colorMap = {
    'green': 'bg-emerald-100 text-emerald-900 border-emerald-300',
    'yellow': 'bg-amber-100 text-amber-900 border-amber-300',
    'orange': 'bg-orange-200 text-orange-900 border-orange-400',
    'red': 'bg-red-200 text-red-900 border-red-400'
  };
  return colorMap[effectiveColor] || 'bg-emerald-100 text-emerald-900';
};

/**
 * Calculate menu position with viewport boundary checks
 * @param {number} clientX - Mouse X position
 * @param {number} clientY - Mouse Y position
 * @param {number} menuWidth - Estimated menu width
 * @param {number} menuHeight - Estimated menu height
 * @returns {Object} {x, y} position for menu
 */
export const calculateMenuPosition = (clientX, clientY, menuWidth = 200, menuHeight = 300) => {
  let x = clientX;
  let y = clientY;
  
  // Check if menu would go off right edge
  if (x + menuWidth > window.innerWidth) {
    x = window.innerWidth - menuWidth - 10;
  }
  
  // Check if menu would go off bottom edge
  if (y + menuHeight > window.innerHeight) {
    y = window.innerHeight - menuHeight - 10;
  }
  
  // Ensure menu doesn't go off left or top edges
  x = Math.max(10, x);
  y = Math.max(10, y);
  
  return { x, y };
};

/**
 * Calculate sum for a group of organisms
 * @param {Array} organisms - List of organisms in the group
 * @param {Object} gridData - Grid data object
 * @param {string} location - Location to calculate sum for
 * @returns {number} Sum value
 */
export const calculateGroupSum = (organisms, gridData, location) => {
  return organisms.reduce((sum, organism) => {
    if (organism.isSum || organism.isTotal) return sum;
    
    const organismKey = `${organism.group}_${organism.species}`;
    const value = gridData[organismKey]?.[location] || '';
    const numValue = parseInt(value) || 0;
    
    return sum + numValue;
  }, 0);
};

/**
 * Initialize grid data for organisms and locations
 * @param {Array} organisms - List of organisms
 * @param {Array} locations - List of locations
 * @returns {Object} Initialized grid data object
 */
export const initializeGridData = (organisms, locations) => {
  const gridData = {};
  
  organisms.forEach(organism => {
    const organismKey = `${organism.group}_${organism.species}`;
    gridData[organismKey] = {};
    
    locations.forEach(location => {
      gridData[organismKey][location] = '';
    });
  });
  
  return gridData;
};

/**
 * Initialize zooplankton grid data
 * @param {Array} zooplanktonOrganisms - List of zooplankton organisms
 * @param {Array} locations - List of locations
 * @returns {Object} Initialized grid data object
 */
export const initializeZooplanktonGridData = (zooplanktonOrganisms, locations) => {
  const gridData = {};
  
  zooplanktonOrganisms.forEach(org => {
    const key = `${org.sampleType}_${org.organism}`;
    gridData[key] = {};
    
    locations.forEach(loc => {
      gridData[key][loc] = '';
    });
  });
  
  return gridData;
};
